import {
  ButtonDS,
  dateFormatterDayMonthYearLong2Digits,
  RemoveRedEyeOutlined,
  SidePanelDS,
  Spacer,
  TextCapitalized,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAppSelector } from "../../../../../redux/hooks";
import {
  LinkedTransaction,
  PublicTransactionsPropsType,
} from "../../transactionsAPI";
import { selectTransactionsList } from "../../transactionsSlice";
import { formatCurrency } from "../../../../../libSlice/materialUiFormats";
import { useMemo } from "react";

type LinkedTransactionBlockProps = {
  transaction: PublicTransactionsPropsType;
  firstPath: string;
};

type LinkedTransactionType = "origin" | "refund";

export const LinkedTransactionBlock = (props: LinkedTransactionBlockProps) => {
  const isRefundTransaction = useMemo(
    () => props.transaction.type === "REFUND",
    [props.transaction],
  );
  const isRefundedExpenseTransaction = useMemo(
    () =>
      props.transaction.type === "EXPENSE" &&
      props.transaction.refundTransaction,
    [props.transaction],
  );

  const hasOriginTransaction = useMemo(
    () => isRefundTransaction && props.transaction.originalTransaction !== null,
    [isRefundTransaction, props.transaction.originalTransaction],
  );

  const hasRefundedTransaction = useMemo(
    () =>
      isRefundedExpenseTransaction &&
      props.transaction.refundTransaction !== null,
    [isRefundedExpenseTransaction, props.transaction.refundTransaction],
  );

  if (!isRefundTransaction && !isRefundedExpenseTransaction) {
    return null;
  }

  if (isRefundedExpenseTransaction && !hasRefundedTransaction) {
    return null;
  }

  const linkedTransaction = hasOriginTransaction
    ? props.transaction.originalTransaction
    : hasRefundedTransaction
      ? props.transaction.refundTransaction
      : null;

  return (
    <>
      {isRefundTransaction && !hasOriginTransaction && (
        <UnidentifiedOriginalTransactionSection />
      )}
      {linkedTransaction && (
        <LinkedTransactionSection
          type={hasOriginTransaction ? "origin" : "refund"}
          transaction={props.transaction}
          linkedTransaction={linkedTransaction}
          firstPath={props.firstPath}
        />
      )}
    </>
  );
};

const UnidentifiedOriginalTransactionSection = () => {
  const { t } = useTranslation();
  return (
    <SidePanelDS.Section horizontalSpacing={1.5} verticalSpacing={2}>
      <StyledTitle>
        <TextCapitalized>
          {t("transactions.linkedTransaction.origin.title")}
        </TextCapitalized>
      </StyledTitle>
      <Spacer y={2} />
      <TextCapitalized>
        {t("transactions.linkedTransaction.origin.unidentified")}
      </TextCapitalized>
    </SidePanelDS.Section>
  );
};

const LinkedTransactionSection = (props: {
  type: LinkedTransactionType;
  transaction: PublicTransactionsPropsType;
  linkedTransaction: LinkedTransaction;
  firstPath: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const initialAmount = Math.abs(props.transaction.amount ?? 0);
  const linkedAmount = Math.abs(props.linkedTransaction.amount);
  const netAmount = Math.max(initialAmount - linkedAmount, 0);

  const transactionsList = useAppSelector(selectTransactionsList);
  const isTransactionLoaded = !!transactionsList?.some(
    (transaction) => transaction.uuid === props.linkedTransaction?.uuid,
  );
  const isRefund = props.type === "refund";

  return (
    <SidePanelDS.Section horizontalSpacing={1.5} verticalSpacing={2}>
      <StyledTitle>
        <TextCapitalized>
          {t(`transactions.linkedTransaction.${props.type}.title`)}
        </TextCapitalized>
      </StyledTitle>
      <Spacer y={2} />
      <SidePanelDS.SectionRow
        label={
          <TextCapitalized>
            {t(`transactions.linkedTransaction.${props.type}.date`)}
          </TextCapitalized>
        }
        value={
          <StyledValue>
            {dateFormatterDayMonthYearLong2Digits(
              new Date(props.linkedTransaction.executionDate),
            )}
          </StyledValue>
        }
      />
      <Spacer y={1.5} />
      <SidePanelDS.SectionRow
        label={
          <TextCapitalized>
            {t(`transactions.linkedTransaction.${props.type}.amount`)}
          </TextCapitalized>
        }
        value={
          <StyledValue>
            {isRefund ? (
              <StyledRefundAmount>
                +{formatCurrency(linkedAmount)}
              </StyledRefundAmount>
            ) : (
              formatCurrency(linkedAmount)
            )}
          </StyledValue>
        }
      />
      {isRefund && (
        <>
          <Spacer y={1.5} />
          <SidePanelDS.SectionRow
            label={
              <TextCapitalized>
                {t("transactions.linkedTransaction.refund.netAmount")}
              </TextCapitalized>
            }
            value={<StyledValue>{formatCurrency(netAmount)}</StyledValue>}
          />
        </>
      )}
      {isTransactionLoaded && (
        <>
          <Spacer y={2} />
          <ButtonDS
            format="fill"
            buttonType="secondary"
            leftIcon={<RemoveRedEyeOutlined />}
            text={t(`transactions.linkedTransaction.${props.type}.button`)}
            onClick={() =>
              navigate(
                `/${props.firstPath}/transactions/cards/${props.linkedTransaction.uuid}`,
              )
            }
          />
        </>
      )}
    </SidePanelDS.Section>
  );
};

const StyledTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledValue = styled.div`
  ${typographies["Body/S"]}
  color: ${colors["colors/text/black"]};
  font-weight: 600;
`;

const StyledRefundAmount = styled.span`
  color: ${colors["colors/system/success/success_normal"]};
`;
